<template>
  <div class="remote-select-pro">
    <a-select
      v-model:value="modalRef.selectedRows"
      :allow-clear="field.allowClear !== false"
      :default-value="field.defaultValue"
      :disabled="field.disabled"
      :filter-option="filterOption"
      :maxTagCount="field.maxTagCount ? field.maxTagCount : 'responsive'"
      :mode="field.mode == 'default' ? null : field.mode"
      :placeholder="placeholder"
      :show-search="field.show_search !== false"
      size="small"
      :autoClearSearchValue="false"
      @change="handleChange"
      @search="handleSearch"
      style="min-width: 120px"
    >
      <a-select-option
        v-for="option in datasource"
        :key="option.value"
        :value="option.value"
      >
        <bold-matcher
          :text="option.label"
          :pattern="searchText"
          :color="field.searchColor"
        />
      </a-select-option>
    </a-select>

    <div
      v-if="field.table?.enable"
      class="table-search-icon"
      style="margin-right: 5px"
    >
      <FileSearchOutlined @click="handleOpenTable" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { debounce, isEqual } from 'lodash-es';
import { convertValueByClassName, isNotEmpty } from 'liyu-pc-base/utils/function';
import BoldMatcher from 'liyu-pc-base/components/FormRender/RemoteSelect/boldenText.vue';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { useRequiredConditionStore, required_condition_event_obj } from 'liyu-pc-base/init-plugin';
import modalBox from 'liyu-pc-base/components/form-modal/modal-tools';
import md5 from 'blueimp-md5';
import { useRoute } from 'vue-router';
import { resolveContextValue } from 'liyu-pc-base/components/FormRender/utils';

defineOptions({
  name: 'RemoteSelectPro',
});

type Field = {
  label: string;
  mode: 'multiple' | 'tags' | 'combobox' | 'default';
  cb?: any;
  select_style: any;
  default: string;
  name: string;
  postUrl: string;
  searchConditions: any;
  queryType: string;
  simpleSearchConditions: any;
  maxTagCount: number;
  allowClear: boolean;
  defaultValue: any;
  disabled: boolean;
  show_search: boolean;
  searchColor: string;
  required_condition: any;
  defaultValueType: string;
  globalDefaultKey: string;
  globalDefaultField: string;
  extendedFields: string[];
  table: any;
};
interface Props {
  field: Field;
  valueKey?: string;
  labelKeyType?: number;
  labelKey?: string;
  labelSpace?: string;
  labelKeyList?: any[];
  value?: Array<any> | number | string | null;
  label?: Array<any> | string;
  formModel: Record<string, any>;
  outModel?: Record<string, any>;
  placeholder?: string;
  init_model?: Record<string, any>;
  is_input?: boolean;
  init_watch?: Record<string, any>;
  is_form_design?: boolean;
  innerModel?: any;
  globalInnerModel?: any;
  context?: Record<string, any> | null;
  baseKeyPath?: any;
}
const props = withDefaults(defineProps<Props>(), {
  labelKeyType: 0,
  labelSpace: '-',
  labelKeyList: () => [],
  value: null,
  label: () => [],
  outModel: () => ({}),
  init_model: () => ({}),
  is_input: false,
  init_watch: () => ({}),
  is_form_design: false,
  innerModel: null,
  globalInnerModel: null,
  context: null,
  baseKeyPath: () => [],
});

const emit = defineEmits<{
  'update:value': [value: any];
  'update:label': [value: any];
}>();
const modalRef = reactive({
  selectedRows: new Array<number | any>(),
  datasource: new Array<any>(),
});
const route = useRoute();
const tableKey = ref('remoteSelectPro');
const tableConfig = ref(null);
const searchText = ref('');
const requiredConditionStore = useRequiredConditionStore();
const getConditionsItem = (value: any, list: any, item: any) => {
  if (item.operator === 'not_blank_not_null') {
    list.push({
      field_name: item.field_name,
      operator: item.operator,
      value: [],
      format: item.format,
      class_name: item.class_name,
    });
  } else if (value instanceof Array) {
    const [first] = value;
    if (isNotEmpty(first)) {
      list.push({
        field_name: item.field_name,
        operator: item.operator,
        value: convertValueByClassName(value, item.class_name),
        format: item.format,
        class_name: item.class_name,
      });
    }
  } else if (isNotEmpty(value)) {
    let val = value;
    if (item.operator === 'in' || item.operator === 'notin') {
      if (value.includes('[')) {
        val = JSON.parse(value);
      } else if (value.includes(',')) {
        val = value.split(',');
      }
      if (item.class_name === 'int') {
        val = val.map(Number);
      }
    }
    list.push({
      field_name: item.field_name,
      operator: item.operator,
      value: convertValueByClassName(val instanceof Array ? val : [val], item.class_name),
      format: item.format,
      class_name: item.class_name,
    });
  }
};

/**
 * 解析相对于 baseKeyPath 的表达式，从 props.context 中获取值。
 * 核心算法已移至 utils.ts 的 resolveContextValue，这里仅做 props 绑定。
 */
const resolveContextValueWrapper = (selectName: string): any => {
  const value = resolveContextValue(selectName, props.context, [
    'formModel',
    ...(props.baseKeyPath || []),
  ]);
  return value;
};

/**
 * searchType === 1 时按 key 同时处理 outModel 与 formModel：
 * 1. outModel 中存在该字段且有值（非 undefined / null）时，优先取 outModel 的值；
 * 2. 否则回退到 formModel 中的同名字段。
 * 注意：不能用 `outModel ? outModel : formModel` 做「对象级」二选一 ——
 * outModel 有默认值 `{}`（恒为真值），且它可能只包含部分字段，
 * 对象级判断会导致 formModel 兜底永远不生效。这里必须做「字段级」查找。
 */
const resolveModelValue = (selectName: any): { found: boolean; value: any } => {
  if (
    props.outModel &&
    Object.prototype.hasOwnProperty.call(props.outModel, selectName) &&
    props.outModel[selectName] !== undefined &&
    props.outModel[selectName] !== null
  ) {
    return { found: true, value: props.outModel[selectName] };
  }
  if (props.formModel && Object.prototype.hasOwnProperty.call(props.formModel, selectName)) {
    return { found: true, value: props.formModel[selectName] };
  }
  return { found: false, value: undefined };
};

const getConditions = (has_search = true) => {
  const conditions = [];
  let search_conditions = [
    [
      {
        searchType: 3,
        field_name: 'is_active',
        operator: 'eq',
        value: [true],
        format: 'choices',
        class_name: 'bool',
      },
    ],
  ];
  if (props.field.searchConditions) {
    search_conditions = props.field.searchConditions;
  }
  search_conditions.forEach((item: any) => {
    const list = [];
    item.forEach((item1: any) => {
      if (item1.searchType === 1) {
        // outModel 优先、formModel 兜底，两者同时参与查找
        const resolved = resolveModelValue(item1.select_name);
        if (resolved.found) {
          getConditionsItem(resolved.value, list, item1);
        }
      } else if (item1.searchType === 2) {
        Object.keys(props.formModel).forEach((key: any) => {
          if (item1.select_name == key) {
            getConditionsItem(props.formModel[key], list, item1);
          }
        });
      } else if (item1.searchType === 3) {
        getConditionsItem(item1.value, list, item1);
      } else if (item1.searchType === 4) {
        getConditionsItem(resolveContextValueWrapper(item1.select_name), list, item1);
      } else {
        getConditionsItem(item1.value, list, item1);
      }
    });
    conditions.push(list);
  });
  if (searchText.value && has_search) {
    searchConfig.pageSize = 50;
    if (props?.labelKeyList && props?.labelKeyList?.length > 0) {
      props.labelKeyList.forEach((key: any, index: any) => {
        let subSearch = conditions[index];
        if (!subSearch) {
          subSearch = [];
          conditions[index] = subSearch;
        }
        subSearch.push({
          field_name: key,
          operator: 'icontains',
          format: 'input',
          value: [searchText.value],
          class_name: 'str',
        });
      });
    } else {
      if (conditions.length === 0) {
        conditions.push([
          {
            field_name: get_label_key(),
            operator: 'icontains',
            format: 'input',
            value: [searchText.value],
            class_name: 'str',
          },
        ]);
      } else {
        for (const item of conditions) {
          item.push({
            field_name: get_label_key(),
            operator: 'icontains',
            format: 'input',
            value: [searchText.value],
            class_name: 'str',
          });
        }
      }
    }
  }
  return conditions;
};
const getSimpleSearchParams = (has_search = true) => {
  const params = {};
  if (props.field.simpleSearchConditions) {
    props.field.simpleSearchConditions.forEach((item: any) => {
      if (item.searchType === 1) {
        // outModel 优先、formModel 兜底，两者同时参与查找
        const resolved = resolveModelValue(item.select_name);
        if (resolved.found) {
          params[item.field_name] = resolved.value;
        }
      } else if (item.searchType === 2) {
        Object.keys(props.formModel).forEach((key: any) => {
          if (item.select_name == key) {
            const value = props.formModel[key];
            params[item.field_name] = value;
          }
        });
      } else if (item.searchType === 3) {
        params[item.field_name] = item.value;
      } else if (item.searchType === 4) {
        params[item.field_name] = resolveContextValueWrapper(item.select_name);
      }
    });
  }
  if (searchText.value && has_search) {
    if (props?.labelKeyList && props?.labelKeyList?.length > 0) {
      props.labelKeyList.forEach((key: any) => {
        params[key] = searchText.value;
      });
    } else {
      params[get_label_key()] = searchText.value;
    }
  }
  return params;
};

const searchConfig: any = {
  current: 1,
  pageSize: 200,
  is_pagination: true,
  request_source: 'strict',
  is_active: true,
  ...props.init_model, // 普通查询条件
  column_fields: [],
  sort_fields: [],
  conditions: [],
  simpleSearchParams: {},
};

const get_value_key = () => {
  return props.valueKey || 'id';
};

const get_label_key = () => {
  return props.labelKey || 'name';
};

const get_sort_fields = () => {
  const list = [];
  if (props?.labelKeyList && props?.labelKeyList?.length > 0 && props.labelKeyType !== 1) {
    list.push(...props.labelKeyList);
  } else {
    list.push(get_label_key());
  }
  return list;
};

const getColumnFields = () => {
  const list = [];
  if (props?.labelKeyList && props?.labelKeyList?.length > 0 && props.labelKeyType !== 1) {
    list.push(get_value_key(), ...props.labelKeyList);
  } else {
    list.push(get_value_key(), get_label_key());
  }
  if (props.field?.extendedFields && props.field?.extendedFields.length > 0) {
    list.push(...props.field.extendedFields);
  }
  // 去重
  const uniqueList = [...new Set(list)];
  searchConfig.column_fields = [].concat(uniqueList);
};

getColumnFields();
searchConfig.sort_fields = get_sort_fields();
if (props.init_model?.sort_fields) {
  searchConfig.sort_fields = props.init_model?.sort_fields;
}

const checkEqualSelected = (value: any) => {
  if (Array.isArray(modalRef.selectedRows)) {
    if (modalRef.selectedRows.includes(value)) {
      return true;
    }
  } else {
    if (modalRef.selectedRows === value) {
      return true;
    }
  }
  return false;
};

/**
 * 对已选值数组去重。
 * 多选模式下，搜索区多次「展开/隐藏」会导致组件反复挂载初始化，
 * 若沿用 push 方式累积，值会重复（如 [1,2] -> [1,2,1,2]），
 * 这里统一在「初始化赋值 / 外部模型同步 / 值上抛」三处出口做去重。
 * 以 String(item) 作为去重键，兼容 id 字段下 1 与 '1' 混用的情况。
 */
const dedupeSelected = (list: any[] | null | undefined): any[] => {
  if (!Array.isArray(list)) {
    return [];
  }
  const seen = new Set<string>();
  const result: any[] = [];
  list.forEach(item => {
    const key = String(item);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(item);
    }
  });
  return result;
};

const datasource = computed(() => {
  return modalRef.datasource.map(item => {
    // 已经选择的数据不处理labelKeyList
    if (props.labelKeyList && props.labelKeyList.length > 0 && props?.labelKeyType !== 1) {
      const label = [];
      for (const key of props.labelKeyList) {
        label.push(item[key as string]);
      }
      return {
        value: item[get_value_key()],
        label: label.join(props.labelSpace),
      };
    } else {
      return {
        value: item[get_value_key()],
        label: item[get_label_key()],
      };
    }
  });
});
const getDefaultValue = (dataList: any) => {
  if (dataList && !Array.isArray(dataList)) {
    dataList = [dataList];
  }
  if (
    props.field.default &&
    !props.formModel[props.field.name] &&
    props.field.mode == 'default' &&
    dataList.length > 0
  ) {
    props.formModel[props.field.name] = dataList[0];
    const foundItem = modalRef.datasource.find(item => {
      if (item[get_value_key()] == dataList[0]) {
        return true;
      }
    });
    if (foundItem) {
      emit('update:label', foundItem[get_label_key()]);
    }
  }
  if (props.field.mode == 'default') {
    const selectItems: any = datasource.value
      .filter((item: any) => checkEqualSelected(item.value))
      .map((item: any) => item.label);
    emit('update:label', selectItems.length > 0 ? selectItems[0] : '');
  } else {
    emit(
      'update:label',
      datasource.value
        .filter((item: any) => checkEqualSelected(item.value))
        .map((item: any) => item.label),
    );
  }
};
const setInnerModel = () => {
  if (props.field.mode === 'default') {
    const rawItems = modalRef.datasource.filter((item: any) =>
      checkEqualSelected(item[get_value_key()]),
    );
    if (props.innerModel) {
      props.innerModel[props.field.name] = rawItems[0];
    }
  } else {
    const rawItems = modalRef.datasource.filter((item: any) =>
      checkEqualSelected(item[get_value_key()]),
    );
    if (props.innerModel) {
      props.innerModel[props.field.name] = rawItems;
    }
  }
};

let oldParams = {};
watch(
  // outModel 参与 searchType===1 的取值（见 resolveModelValue），必须一并监听：
  // 否则外部模型变化时查询条件不会重建。下方 isEqual(oldParams, newParams) 会比对新旧参数，
  // 参数没变就不会真的刷新，不会多打请求。
  () => [props.formModel, props.context, props.outModel],
  () => {
    const newParams = getQueryParams(false);
    if (!isEqual(oldParams, newParams)) {
      oldParams = newParams;
      delayRefresh(true);
    }
  },
  { deep: true },
);

watch(
  () => props.formModel[props.field.name],
  () => {
    if (props.field.mode === 'default') {
      if (props.formModel[props.field.name] || props.formModel[props.field.name] === 0) {
        modalRef.selectedRows = [props.formModel[props.field.name]];
      } else {
        modalRef.selectedRows = [];
      }
      const selectItems: any = datasource.value
        .filter((item: any) => checkEqualSelected(item.value))
        .map((item: any) => item.label);
      emit('update:label', selectItems.length > 0 ? selectItems[0] : '');
    } else {
      // 注意：赋值时必须用「去重后的拷贝」，不能直接引用 formModel 里的原数组，
      // 否则组件内部数组会与外部 searchConditions 的值数组共享同一引用，
      // 一旦后续再对 selectedRows 做任何原地修改（如 push），会反向污染外部模型造成值重复
      if (props.formModel[props.field.name] && props.formModel[props.field.name].length > 0) {
        modalRef.selectedRows = dedupeSelected([...props.formModel[props.field.name]]);
      } else {
        modalRef.selectedRows = [];
      }
      emit(
        'update:label',
        datasource.value
          .filter((item: any) => checkEqualSelected(item.value))
          .map((item: any) => item.label),
      );
    }
  },
);

watch(
  () => props.value,
  () => {
    setInnerModel();
  },
  { deep: true },
);

const handleRequiredConditionChange = (key: string) => {
  if (props.field.required_condition) {
    const item = props.field.required_condition.find((item: any) => item.key === key);
    if (item) {
      delayRefresh(true);
    }
  }
};

onMounted(() => {
  let tmpKey = `${route.path}_${props.field.name}_${props.field.postUrl}`;
  tableKey.value = 'remoteselectpro:' + md5(tmpKey);
  initDataSource();
  required_condition_event_obj().on('required_condition_change', handleRequiredConditionChange);
  refresh();
});

onUnmounted(() => {
  required_condition_event_obj().off('required_condition_change', handleRequiredConditionChange);
});
const initDataSource = () => {
  // 统一的值转换：valueKey 为 id 时，尽量转成 Number，与历史逻辑保持一致
  const convertValue = (item: any) => {
    if (get_value_key() === 'id') {
      if (isNaN(Number(item))) {
        return item;
      } else {
        return Number(item);
      }
    } else {
      return item;
    }
  };
  let list: any[] = [];
  if (props.field.mode === 'default') {
    if (props.value || props.value === 0) {
      list.push(convertValue(props.value));
    }
  } else {
    if (typeof props.value === 'string') {
      list = (props.value as string).split(',').map(convertValue);
    } else if (props.value instanceof Array) {
      list = props.value.map(convertValue);
    } else if (props.value != null) {
      list.push(convertValue(props.value));
    }
  }
  // 必须用「去重后赋值」替代原 push 逻辑：
  // 该组件在搜索区反复显示/隐藏时会多次挂载，每次挂载都会执行到这里，
  // 若 push 到已存在值的数组上（或与外部模型共享引用的数组上），多选值会重复累积
  modalRef.selectedRows = dedupeSelected(list);
};
const handleChange = () => {
  if (props.field.mode == 'default') {
    emit('update:value', modalRef.selectedRows);
    const selectItems: any = datasource.value
      .filter((item: any) => {
        return modalRef.selectedRows == item.value;
      })
      .map((item: any) => item.label);
    emit('update:label', selectItems.length > 0 ? selectItems[0] : '');
  } else {
    // 出口兜底去重：antd 多选对已有重复值时仍会按 [...mergedValues, val] 追加，
    // 这里先对值去重再上抛，保证外部 searchConditions/formModel 中不会出现重复
    modalRef.selectedRows = dedupeSelected(modalRef.selectedRows);
    emit('update:value', modalRef.selectedRows);
    emit(
      'update:label',
      datasource.value
        .filter((item: any) => checkEqualSelected(item.value))
        .map((item: any) => item.label),
    );
  }
};
const getRequiredConditionObj = () => {
  const required_condition = props.field.required_condition;
  const required_condition_obj = {};
  if (required_condition) {
    required_condition.forEach((item: any) => {
      const value = requiredConditionStore.value_map[item.key];
      if (value !== undefined && value !== null) {
        required_condition_obj[item.field_name] = value;
      }
    });
  }
  return required_condition_obj;
};

/**
 * 补充之前选择的项对应的数据
 */
const externDataSource = async (params: any, selectedRows: any) => {
  if (selectedRows && !Array.isArray(selectedRows)) {
    selectedRows = [selectedRows];
  }
  if (selectedRows && selectedRows.length > 0) {
    const notFoundValues = [];
    selectedRows.forEach((item1: any) => {
      if (!modalRef.datasource.some(item => item[get_value_key()] === item1)) {
        notFoundValues.push(item1);
      }
    });
    if (notFoundValues.length > 0) {
      const obj = { ...params };
      if (props.field.queryType === 'simple') {
        let value = notFoundValues as any;
        if (value instanceof Array && value.length > 0) {
          value = value.join(',');
        }
        obj[get_value_key()] = value;
      } else {
        if (obj.conditions && obj.conditions.length > 0) {
          obj.conditions[0].push({
            class_name: 'int',
            operator: 'in',
            value: notFoundValues,
            format: 'remote',
            field_name: get_value_key(),
          });
        } else {
          obj.conditions = [[]];
          obj.conditions[0].push({
            class_name: 'int',
            operator: 'in',
            value: notFoundValues,
            format: 'remote',
            field_name: get_value_key(),
          });
        }
      }
      const res1 = await getApiData({ ...obj }, props.field.postUrl, false, {
        skip_error: true,
      });
      modalRef.datasource = modalRef.datasource.concat(res1?.data || []);
    }
  }
};
const getQueryParams = (has_search = true) => {
  let params: any = {};
  if (props.field.queryType === 'simple') {
    searchConfig.simpleSearchParams = getSimpleSearchParams(has_search);
    params = {
      ...searchConfig,
      ...searchConfig.simpleSearchParams,
      required_condition: getRequiredConditionObj(),
    };
    delete params.conditions;
    delete params.simpleSearchParams;
  } else {
    searchConfig.conditions = getConditions(has_search);
    params = { ...searchConfig, required_condition: getRequiredConditionObj() };
    delete params.simpleSearchParams;
  }
  return params;
};

const refresh = async (clear = false) => {
  // modalRef.datasource.length = 0;
  // 不清空modalRef.datasource，保留原始数据，如果是输入过滤条件筛选的数据，需要去重后，追加到datasource里
  if (props.field.postUrl) {
    if (clear) {
      modalRef.datasource.length = 0;
    }
    const is_first = modalRef.datasource.length === 0;
    const params = getQueryParams();
    const res = await getApiData(params, props.field.postUrl, false, { skip_error: true });
    if (res?.data?.length > 0) {
      const rowKey = get_value_key();
      const new_list = res?.data.filter(i => {
        const f = modalRef.datasource.find(j => {
          return i[rowKey] == j[rowKey];
        });
        return !f;
      });
      modalRef.datasource = modalRef.datasource.concat(new_list);
      if (is_first) {
        await externDataSource(params, modalRef.selectedRows);
      }
      setInnerModel();
    }

    if (props.field.defaultValueType === 'first' && !props.is_form_design) {
      if (!props.formModel[props.field.name]) {
        if (props.field.mode === 'default' || !props.field.mode) {
          emit('update:label', modalRef.datasource[0][get_label_key()]);
          emit('update:value', modalRef.datasource[0][get_value_key()]);
        } else {
          emit('update:label', [modalRef.datasource[0][get_label_key()]]);
          emit('update:value', [modalRef.datasource[0][get_value_key()]]);
        }
      }
    } else if (props.field.defaultValueType === 'global' && !props.is_form_design) {
      if (!props.formModel[props.field.name]) {
        const globalDefaultKey = props.field.globalDefaultKey;
        const globalDefaultField = props.field.globalDefaultField;
        const globalDefaultValue = requiredConditionStore.value_map[globalDefaultKey];
        if (globalDefaultValue) {
          const globalDefaultItem = modalRef.datasource.find(
            item1 => item1[globalDefaultField] === globalDefaultValue,
          );
          if (globalDefaultItem) {
            if (props.field.mode === 'default' || !props.field.mode) {
              emit('update:label', globalDefaultItem[get_label_key()]);
              emit('update:value', globalDefaultItem[get_value_key()]);
            } else {
              emit('update:label', [globalDefaultItem[get_label_key()]]);
              emit('update:value', [globalDefaultItem[get_value_key()]]);
            }
          }
        }
      }
    } else {
      getDefaultValue(modalRef.selectedRows);
    }
  }
};
const delayRefresh: any = debounce((clear = false) => {
  refresh(clear);
}, 500);

watch(
  () => [
    props.labelKeyList,
    props.labelKey,
    props.labelKeyType,
    props.valueKey,
    props.labelSpace,
    props.field.postUrl,
  ],
  (newValue, oldValue) => {
    if (!isEqual(newValue, oldValue)) {
      getColumnFields();
      searchConfig.sort_fields = get_sort_fields();
      delayRefresh(true);
    }
  },
  { deep: true },
);

watch(
  () => [props.field.searchConditions, props.field.queryType, props.field.simpleSearchConditions],
  (newValue, oldValue) => {
    if (!isEqual(newValue, oldValue)) {
      delayRefresh(true);
    }
  },
  { deep: true },
);

const handleSearch = (str: any) => {
  searchText.value = str;
  delayRefresh();
};

const tableModal = defineAsyncComponent(() => import('./tableModal.vue'));

const handleOpenTable = () => {
  let currentValue = [];
  searchText.value = '';
  let rowSelectionType = 'checkbox';
  if (props.field.mode === 'default') {
    rowSelectionType = 'radio';
  }
  if (!datasource.value) {
    currentValue = [];
  } else {
    currentValue = datasource.value.filter((item: any) => checkEqualSelected(item.value));
  }
  tableConfig.value = {
    key: tableKey.value,
    title: props.field.label || props.field.name,
    rowSelectionType,
    columns: props.field.table?.columns || [],
    search_config: props.field.table?.search_config || [],
    list_url: props.field.postUrl,
    is_selection: true,
    is_pagination: true,
    hide_header: true,
  };
  const init_model = props.init_model || {};
  if (props.field.queryType === 'simple') {
    tableConfig.value['query_params'] = { ...init_model, ...getSimpleSearchParams() };
  } else {
    const conditions = getConditions();
    tableConfig.value['query_params'] = init_model;
    if (conditions.length > 0 && conditions[0].length > 0) {
      tableConfig.value['base_conditions'] = conditions[0];
    }
  }
  const required_condition = getRequiredConditionObj();
  if (required_condition) {
    tableConfig.value['query_params'] = {
      ...tableConfig.value['query_params'],
      required_condition,
    };
  }
  const tempModal = reactive({
    open: true,
    loading: false,
    component: tableModal,
    isMultiple: props.field.mode !== 'default',
    tableConfig: tableConfig.value,
    labelKey: props.labelKey || 'name',
    value: currentValue,
    labelKeyList: props.labelKeyList || [],
    labelKeyType: props.labelKeyType || 1,
    labelSpace: props.labelSpace || '-',
    valueKey: props.valueKey || 'id',
    ok: async (data: any) => {
      const selectedValues = data.map((item: any) => item.value) || [];
      const selectedLabels = data.map((item: any) => item.label) || [];
      const params = getQueryParams();
      if (props.field.mode == 'default') {
        if (selectedValues.length == 0) {
          modalRef.selectedRows = undefined;
          emit('update:value', modalRef.selectedRows);
          emit('update:label', undefined);
        } else {
          modalRef.selectedRows = selectedValues[0];
          await externDataSource(params, selectedValues[0]);
          emit('update:value', modalRef.selectedRows);
          emit('update:label', selectedLabels[0]);
        }
      } else {
        modalRef.selectedRows = selectedValues;
        await externDataSource(params, selectedValues);
        emit('update:value', modalRef.selectedRows);
        emit('update:label', selectedLabels);
      }
    },
    cancel: () => {
      console.log('cancel');
    },
  });
  modalBox(tempModal);
};

const filterOption = (val: string, option: any) => {
  if (datasource.value instanceof Array) {
    const f: any = datasource.value.find(item => {
      return item['value'] == option.key;
    });
    if (f) {
      return String(f['label']).toLowerCase().indexOf(val.toLowerCase()) >= 0;
    } else {
      return false;
    }
  }
};
</script>
<style scoped lang="less">
.remote-select-pro {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  flex-wrap: nowrap;
  // 禁用状态下文字保持黑色，不显示为灰色
  :deep(.ant-select-disabled.ant-select:not(.ant-select-customize-input) .ant-select-selector) {
    color: #000;
  }
  :deep(.ant-select-disabled .ant-select-selection-item) {
    color: #000;
  }
}
.ant-input-group.ant-input-group-compact {
  .remote-select-pro {
    display: flex;
  }
}
.ant-form-inline {
  .ant-input-group.ant-input-group-compact {
    .remote-select-pro {
      display: inline-flex;
    }
  }
}
.table-search-icon {
  color: black;
  font-size: 17px;
}
</style>
